<section class="min-h-screen">
  <!-- Header -->
  <div class="mb-6 flex items-center gap-3">
    <button (click)="goBack()" class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <div>
      <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">
        {{ isEditMode() ? 'Edit Form' : 'New Application Form' }}
      </h1>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">Configure fields for this institution + product combination</p>
    </div>
  </div>

  @if (isLoading()) {
    <div class="flex items-center justify-center py-24"><app-progress-loader></app-progress-loader></div>
  } @else {
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- Left: Institution + Product selector + Field list -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Step 1: Select Institution + Product -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">1. Select Institution & Product</p>
          <form [formGroup]="selectorForm" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Form Name <span class="text-error">*</span></label>
              <input formControlName="name" type="text" placeholder="e.g. Personal Loan Application"
                class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 placeholder:text-slate-400">
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Description</label>
              <input formControlName="description" type="text" placeholder="Optional description..."
                class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 placeholder:text-slate-400">
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Institution</label>
              <select formControlName="institutionId"
                [attr.disabled]="isEditMode() ? true : null"
                class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <option value="">Select institution...</option>
                @for (inst of institutions(); track inst.id) {
                  <option [value]="inst.id">{{ inst.name }}</option>
                }
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Product</label>
              <select formControlName="productId"
                [attr.disabled]="isEditMode() || !selectorForm.get('institutionId')?.value ? true : null"
                class="w-full h-10 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <option value="">Select product...</option>
                @for (product of products(); track product.id) {
                  <option [value]="product.id">{{ product.name }}</option>
                }
              </select>
            </div>
            @if (isEditMode()) {
              <p class="col-span-2 text-[10px] text-slate-400 mt-1">Institution and product cannot be changed when editing an existing form.</p>
            }
          </form>
        </div>

        <!-- Step 2: Field List -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">2. Form Fields</p>

          @if (isLoadingFields()) {
            <div class="flex items-center justify-center py-10"><app-progress-loader></app-progress-loader></div>
          } @else if (defaultFieldsError()) {
            <div class="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900">
              <p class="text-xs text-red-600 dark:text-red-400">Failed to load default fields. Please try selecting the institution and product again.</p>
            </div>
          } @else if (fields().length === 0) {
            <p class="text-xs text-slate-400 text-center py-8">No fields loaded. Try refreshing the page.</p>
          } @else {
            <div class="space-y-2">
              @for (field of fields(); track field.key; let i = $index) {
                <div class="flex items-center gap-3 p-3 rounded-lg border"
                  [class]="field.isDefault
                    ? 'bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-600'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600'">

                  <!-- Reorder -->
                  <div class="flex flex-col gap-0.5 shrink-0">
                    <button (click)="moveField(i, 'up')" [disabled]="i === 0"
                      class="p-0.5 rounded text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 disabled:opacity-20 transition-colors">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7"/></svg>
                    </button>
                    <button (click)="moveField(i, 'down')" [disabled]="i === fields().length - 1"
                      class="p-0.5 rounded text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 disabled:opacity-20 transition-colors">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                    </button>
                  </div>

                  <!-- Field info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <p class="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{{ field.label }}</p>
                      @if (field.isDefault) {
                        <span class="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-500 dark:text-blue-400 border border-blue-100 dark:border-blue-900 uppercase tracking-wider">default</span>
                      }
                    </div>
                    <p class="text-[10px] text-slate-400 mt-0.5">Type: {{ field.type | lowercase }}  ·  Key: {{ field.key }}</p>
                    @if ((field.type === 'SELECT' || field.type === 'MULTI_SELECT') && !field.isDefault) {
                      <div class="mt-2 flex flex-wrap gap-1">
                        @for (option of field.options; track option.value) {
                          <span class="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] text-slate-600 dark:text-slate-300">
                            {{ option.label }} ({{ option.value }})
                            <button (click)="removeOption(field.key, option.value)" class="text-slate-400 hover:text-red-500 transition-colors">×</button>
                          </span>
                        }
                        <div class="flex items-center gap-1">
                          <input [(ngModel)]="getNewOption(field.key).label" type="text" placeholder="Label..."
                            class="h-5 px-1.5 text-[10px] border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none w-20">
                          <input [(ngModel)]="getNewOption(field.key).value" type="text" placeholder="Value..."
                            class="h-5 px-1.5 text-[10px] border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none w-20">
                          <button (click)="addOption(field.key)" class="text-[10px] text-primary-800 dark:text-primary-400 font-semibold hover:underline">Add</button>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Required toggle -->
                  <div class="flex items-center gap-1.5 shrink-0">
                    <span class="text-[10px] text-slate-400">Required</span>
                    <button (click)="toggleDefaultRequired(field)"
                      class="relative w-7 h-4 rounded-full transition-colors"
                      [class]="field.required ? 'bg-primary-800' : 'bg-slate-200 dark:bg-slate-600'">
                      <span class="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
                        [class]="field.required ? 'translate-x-3.5' : 'translate-x-0.5'"></span>
                    </button>
                  </div>

                  <!-- Delete (custom only) -->
                  @if (!field.isDefault) {
                    <button (click)="removeCustomField(field.key)"
                      class="shrink-0 p-1 rounded text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Right: Add custom field panel + Save -->
      <div class="space-y-5">

        <!-- Add custom field -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Add Custom Field</p>
          <form [formGroup]="addFieldForm" class="space-y-3">
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Label</label>
              <input formControlName="label" type="text" placeholder="e.g. Monthly Income"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 placeholder:text-slate-400">
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Type</label>
              <select formControlName="type"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100">
                @for (type of fieldTypes; track type) {
                  <option [value]="type">{{ type }}</option>
                }
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Placeholder</label>
              <input formControlName="placeholder" type="text" placeholder="Optional hint text..."
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800/20 text-slate-800 dark:text-slate-100 placeholder:text-slate-400">
            </div>
            <div class="flex items-center gap-2">
              <input formControlName="required" type="checkbox" id="req" class="rounded">
              <label for="req" class="text-xs font-semibold text-slate-600 dark:text-slate-300">Required</label>
            </div>
            <button type="button" (click)="addCustomField()" [disabled]="addFieldForm.invalid"
              class="w-full py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              + Add Field
            </button>
          </form>
        </div>

        <!-- Save -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Save Form</p>
          @if (defaultFieldsError()) {
            <p class="text-[11px] text-red-500 dark:text-red-400 mb-3">Fix the default fields error before saving.</p>
          }
          <button (click)="save()" [disabled]="!canSave() || isSaving()"
            class="w-full py-2.5 rounded-lg text-xs font-bold bg-primary-800 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {{ isSaving() ? 'Saving...' : (isEditMode() ? 'Update Form' : 'Create Form') }}
          </button>
        </div>
      </div>
    </div>
  }
</section>

<!-- Success dialog -->
@if (showSuccessDialog()) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
    <div class="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-300">
      <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/40 via-primary-800 to-primary/40"></div>
      <div class="p-8 text-center">
        <div class="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-1">Form {{ isEditMode() ? 'Updated' : 'Created' }}!</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-5">Share this link with your customers to collect applications.</p>
        <div class="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 mb-5">
          <p class="flex-1 text-xs text-slate-700 dark:text-slate-300 truncate">{{ shareableLink }}</p>
          <button (click)="copyShareableLink()"
            class="shrink-0 px-2.5 py-1 rounded text-[10px] font-semibold bg-primary-800 text-white hover:bg-primary-700 transition-colors">
            Copy
          </button>
        </div>
        <button (click)="closeSuccessDialog()"
          class="w-full py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
          Back to Forms
        </button>
      </div>
    </div>
  </div>
}
